import { type RefObject, useCallback, useEffect, useRef } from "react";
import type { Task } from "@common/types/task";
import type { BoardPlacement } from "@design-system/Board/Board.types";
import {
    findCellCards,
    findItemCard,
} from "@design-system/Board/boardAttributes";
import { TASK_BOARD_DRAFT_ID } from "./taskBoardDraft";

interface DraftOrigin {
    /** What had focus when the draft opened: the card N was pressed on, or the `+`. */
    element: HTMLElement | null;
    placement: BoardPlacement;
}

/** A created task's card to focus when it lands, unless focus moved on from `from`. */
interface PendingFocus {
    taskId: string;
    from: Element | null;
}

const getFocusedElement = () =>
    document.activeElement instanceof HTMLElement &&
    document.activeElement !== document.body
        ? document.activeElement
        : null;

/** The first card other than the draft in a cell. */
const findFirstCellCard = (root: HTMLElement, placement: BoardPlacement) => {
    const draftCard = findItemCard(root, TASK_BOARD_DRAFT_ID);
    return (
        findCellCards(root, placement).find((card) => card !== draftCard) ??
        null
    );
};

/**
 * The draft's origin while it's on the page, else the first card in its
 * cell, else the board's wrapper (a click focuses nothing in Safari and
 * Firefox on macOS, and the cell may be empty).
 */
const getReturnTarget = (origin: DraftOrigin, root: HTMLElement) =>
    origin.element?.isConnected
        ? origin.element
        : (findFirstCellCard(root, origin.placement) ?? root);

/**
 * Where focus goes when a draft card ends from the keyboard, since the draft
 * unmounts: back to where it was opened from (else the cell's first card,
 * else the board's wrapper), then to the created card in the tasks update
 * after the create. A created task that update doesn't show on the board
 * (filtered out, or in a collapsed column or lane) takes no focus later. A
 * draft left with the mouse leaves focus where the user put it.
 */
export const useTaskBoardDraftFocus = ({
    rootRef,
    tasks,
}: {
    rootRef: RefObject<HTMLElement | null>;
    /** Its first change after a create settles where focus goes. */
    tasks: readonly Task[];
}) => {
    const originRef = useRef<DraftOrigin | null>(null);
    /** Where Enter or Escape put focus (null: nowhere); undefined until then. */
    const returnedToRef = useRef<HTMLElement | null | undefined>(undefined);
    const pendingFocusRef = useRef<PendingFocus | null>(null);

    useEffect(() => {
        const root = rootRef.current;
        const pending = pendingFocusRef.current;
        pendingFocusRef.current = null;
        const card =
            root === null || pending === null
                ? null
                : findItemCard(root, pending.taskId);
        if (card !== null && getFocusedElement() === pending?.from) {
            card.focus({ preventScroll: true });
        }
    }, [rootRef, tasks]);

    const rememberOrigin = useCallback((placement: BoardPlacement) => {
        originRef.current = { element: getFocusedElement(), placement };
        returnedToRef.current = undefined;
        pendingFocusRef.current = null;
    }, []);

    const returnFocus = useCallback(() => {
        const origin = originRef.current;
        const root = rootRef.current;
        const target =
            origin === null || root === null
                ? null
                : getReturnTarget(origin, root);
        returnedToRef.current = target;
        target?.focus({ preventScroll: true });
    }, [rootRef]);

    /** At submit: what to call with the created task's id, or null when the draft was left with the mouse. */
    const getCreatedFocus = useCallback(() => {
        const returnedTo = returnedToRef.current;
        return returnedTo === undefined
            ? null
            : (taskId: string) => {
                  pendingFocusRef.current = { taskId, from: returnedTo };
              };
    }, []);

    return { rememberOrigin, returnFocus, getCreatedFocus };
};
